import { HStack, IconButton, Text, DataList, DataItem } from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { cloneDeep } from 'lodash';
import { eventNotificationPriorityToColors } from '../../../event-notifications/event-notifications';
import type { AmlCallView, PhoneCallNotification, PhoneCallView } from '../../../phone-calls/types';
import { usePhoneCallsStore } from '../../../phone-calls/use-phone-calls';
import { getCallerAddressLabel } from '../../../phone-calls/helpers';
import { useCallImport } from './use-call-import';
import { useViewCallOnMap } from './use-view-call-on-map';
import { useFormContext } from 'react-hook-form';
import { useCallback } from 'react';

const NotifiableCallProperty = observer(function NotifiableCallProperty(props: {
    label: string;
    value: string | undefined;
    selectedCall: PhoneCallView | AmlCallView;
    phoneCallNotification: PhoneCallNotification;
}): React.ReactElement {
    const phoneCallsStore = usePhoneCallsStore();
    const isNotifying = phoneCallsStore.getPhoneCallNotification(
        props.selectedCall.phoneNumberId!,
        props.phoneCallNotification
    );
    return (
        <DataList>
            <DataItem
                dataKey={props.label}
                onClick={() => {
                    phoneCallsStore.removePhoneCallNotifications(props.selectedCall.phoneNumberId!);
                }}
            >
                <Text
                    style={{
                        cursor: isNotifying ? 'pointer' : undefined,
                        color: isNotifying
                            ? eventNotificationPriorityToColors['STANDARD'].notchOrText
                            : undefined,
                    }}
                >
                    {props.value}
                </Text>
            </DataItem>
        </DataList>
    );
});

export const PhoneCallInfoDisplay = observer(function CallInfoDisplay(props: {
    eventExists: boolean;
    selectedCall: PhoneCallView | AmlCallView;
    phoneCall: PhoneCallView;
    isAmlCall?: boolean;
}): React.ReactElement {
    const { eventExists, selectedCall, phoneCall, isAmlCall } = props;
    const callImport = useCallImport(eventExists);
    const callDisplay = selectedCall.normalizedPhoneCallDisplay;
    const viewCallOnMap = useViewCallOnMap();
    const form = useFormContext();

    const handelViewCallOnMap = useCallback(
        function handelViewCallOnMap() {
            const eventId = form.getValues('eventId');
            const formLocation = form.getValues('location');
            const location = formLocation && !eventId ? cloneDeep(formLocation) : undefined;

            isAmlCall
                ? viewCallOnMap({
                      amlCallView: selectedCall as AmlCallView,
                      location,
                      shouldFocusAml: true,
                  })
                : viewCallOnMap({
                      phoneCallView: { ...phoneCall, matchedActiveEventId: eventId },
                      location,
                  });
        },
        [viewCallOnMap, isAmlCall, selectedCall, phoneCall, form]
    );

    return (
        <>
            <Text variant="headingXs" color="text.placeholder">
                Caller Info
            </Text>
            {callDisplay.pilotNumberDisplay ? (
                <DataList>
                    <DataItem dataKey="Pilot #">{callDisplay.pilotNumberDisplay}</DataItem>
                </DataList>
            ) : undefined}
            <HStack pl={3}>
                <IconButton
                    variant="ghost"
                    isDisabled={eventExists || !callDisplay.callerName}
                    icon="TransmitData"
                    aria-label="import call reporting party"
                    size="sm"
                    onClick={() => {
                        callImport.importReportingPartyName(selectedCall);
                    }}
                />
                <NotifiableCallProperty
                    label="Reporting Party"
                    value={callDisplay.callerName}
                    selectedCall={selectedCall}
                    phoneCallNotification="CALLER_NAME"
                />
            </HStack>
            <HStack pl={3}>
                <IconButton
                    variant="ghost"
                    isDisabled={eventExists || !callDisplay.callerPhoneNumber}
                    icon="TransmitData"
                    aria-label="import call phone number"
                    size="sm"
                    onClick={() => {
                        callImport.importReportingPartyPhoneNumber(selectedCall);
                    }}
                />
                <NotifiableCallProperty
                    label="Phone"
                    value={callDisplay.callerPhoneNumber}
                    selectedCall={selectedCall}
                    phoneCallNotification="CALLER_PHONE_NUMBER"
                />
            </HStack>
            <Text variant="headingXs" color="text.placeholder">
                Location
            </Text>
            {callDisplay.classOfServiceAndUncertaintyConfidenceDisplay ? (
                <DataList>
                    <DataItem dataKey="Class of Service">
                        {callDisplay.classOfServiceAndUncertaintyConfidenceDisplay}
                    </DataItem>
                </DataList>
            ) : undefined}
            <HStack pl={3}>
                <IconButton
                    variant="ghost"
                    icon="TransmitData"
                    aria-label="import call address"
                    size="sm"
                    disabled={!callDisplay.callerAddressDisplay}
                    onClick={() => {
                        callImport.importAddress(selectedCall);
                    }}
                />
                <NotifiableCallProperty
                    label={getCallerAddressLabel(phoneCall.classOfService)}
                    value={callDisplay.callerAddressDisplay}
                    selectedCall={selectedCall}
                    phoneCallNotification="CALLER_ADDRESS"
                />
            </HStack>
            <HStack pl={3}>
                <IconButton
                    variant="ghost"
                    icon="TransmitData"
                    aria-label="import caller gps"
                    size="sm"
                    disabled={!callDisplay.callerGps}
                    onClick={() => {
                        callImport.importGps(selectedCall);
                    }}
                />
                <NotifiableCallProperty
                    label="Caller GPS"
                    value={callDisplay.callerGps}
                    selectedCall={selectedCall}
                    phoneCallNotification="CALLER_GPS"
                />
                <IconButton
                    data-testid="MAP_CROSSHAIRS_BUTTON"
                    variant="ghost"
                    icon="Crosshairs"
                    aria-label="Location"
                    size="sm"
                    onClick={handelViewCallOnMap}
                />
            </HStack>
        </>
    );
});
